import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/landing/Logo";
import { Footer } from "@/components/landing/Footer";

const H = ({ children }) => <h2 className="mt-12 font-display text-3xl font-extrabold uppercase sm:text-4xl">{children}</h2>;
const P = ({ children }) => <p className="mt-4 leading-relaxed text-[#c4c8cf]">{children}</p>;
const UL = ({ items }) => (
  <ul className="mt-4 list-disc space-y-2.5 pl-5 leading-relaxed text-[#c4c8cf] marker:text-[#ff6a3d]">
    {items.map((t, i) => <li key={i}>{t}</li>)}
  </ul>
);
const B = ({ children }) => <strong className="text-[#f2f3f5]">{children}</strong>;
const MAIL = "isaacconteh26@gmail.com";
const Mail = ({ subject }) => (
  <a href={`mailto:${MAIL}${subject ? `?subject=${encodeURIComponent(subject)}` : ""}`} className="text-[#ff8a3d] underline-offset-4 hover:underline">{MAIL}</a>
);

const ROWS = [
  ["Your name (or nickname), weight class, bodyweight, experience level, goals, calorie settings and an optional profile photo", "Your phone and your Spotter (online)", "To suggest starting weights, build workouts and show your buddy who's who"],
  ["Workouts and activities you log (exercises, weights, reps, minutes, distance, notes), today's workout choice, weigh-ins, water, meal plan, shopping list and steps you enter", "Your phone and your Spotter (online)", "So your history, progress and plan sync between your phones and your training buddy"],
  ["Food diary entries", "Your phone only", "To count your calories and macros"],
  ["Foods you scan or recipes you create (name, nutrition, optional small photo)", "Your Spotter (online)", "So everyone in your Spotter can reuse them"],
  ["Body-scan and progress photos", "Your phone only", "To compare your progress over time. The scan measurements are worked out on your phone."],
];

const StoreTable = () => (
  <div className="mt-6 overflow-x-auto rounded-2xl border border-white/[0.08] bg-[#16181f]">
    <table data-testid="privacy-table" className="w-full min-w-[640px] text-left text-sm">
      <thead className="font-mono-x text-xs uppercase tracking-widest text-[#9ca3af]">
        <tr><th className="p-4">Information</th><th className="p-4">Where it's kept</th><th className="p-4">Why</th></tr>
      </thead>
      <tbody>
        {ROWS.map(([a, b, c]) => (
          <tr key={a} className="border-t border-white/[0.07] align-top text-[#c4c8cf]">
            <td className="p-4">{a}</td><td className="p-4 text-[#f2f3f5]">{b}</td><td className="p-4">{c}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function PrivacyPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Spotter Privacy Policy";
    window.scrollTo(0, 0);
    return () => { document.title = prev; };
  }, []);

  return (
    <div data-testid="privacy-page" className="min-h-screen bg-[#0e0f12] text-[#f2f3f5]">
      <header className="border-b border-white/[0.06]">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-5"><Logo testId="privacy-logo" /><Link to="/" data-testid="privacy-back-link" className="text-sm text-[#c4c8cf] hover:text-[#ff8a3d]">← Back to Spotter</Link></div>
      </header>
      <article className="mx-auto max-w-3xl px-5 py-16 text-base">
        <p className="font-mono-x text-xs uppercase tracking-[0.25em] text-[#ff8a3d]">Privacy policy</p>
        <h1 data-testid="privacy-title" className="mt-4 font-display text-5xl font-black uppercase leading-[0.9] sm:text-6xl">Spotter Privacy Policy</h1>
        <P>Spotter is made by Breezy Labs (Isaac Conteh), Adelaide, Australia. Last updated 17 October 2026.</P>

        <div className="mt-10 rounded-2xl border border-[#ff6a3d]/30 bg-[#ff6a3d]/[0.06] p-6">
          <p className="font-semibold">The short version</p>
          <UL items={[
            <>Spotter is free. There are <B>no ads, no tracking and no analytics</B>, and we never sell your information.</>,
            <>There's no email sign-up or password. Your training is shared through a <B>Spotter code</B> with the people you invite.</>,
            <>Your food diary, progress photos and body-scan photos <B>stay on your phone</B>.</>,
            <>Apple Watch, iPhone steps, reminders and the AI coach are all <B>optional</B>. Spotter works fully without them.</>,
          ]} />
        </div>

        <H>1. What Spotter stores, and where</H>
        <StoreTable />
        <P>"Your Spotter (online)" means one private file for your group, stored with our hosting provider Vercel in Sydney, Australia. <B>Anyone with your Spotter code or invite link can see and change your Spotter's shared information</B>, so only share it with people you train with.</P>

        <H>2. Optional features that send information to other services</H>
        <UL items={[
          <><B>AI coach (body-scan plan and weekly check-in).</B> Only when you tap the button and agree, Spotter sends the photo you chose (and a goal photo or name if you add one) plus your measurements, recent weights and calorie averages to Anthropic's Claude AI to write your plan. Anthropic processes it to give the answer. Spotter does not save these photos on its servers. A general description of a named goal physique (not your photo) may be saved to speed up the next request.</>,
          <><B>Barcode and food search.</B> The barcode or search words are sent to Open Food Facts, a free public food database, to look up nutrition.</>,
          <><B>Exercise videos.</B> Videos are YouTube videos played in privacy-enhanced mode (youtube-nocookie.com). YouTube's own privacy policy applies when you play one.</>,
          <><B>Phone reminders.</B> If you turn them on, your phone's push address, time zone and reminder times are stored so we can send them. Turn them off in Settings at any time.</>,
          <><B>Apple Watch and iPhone steps (Shortcuts).</B> If you set these up, your iPhone sends workout calories, heart rate or step counts to your Spotter through a private link. You can make a new link at any time, which stops the old one.</>,
          <><B>Beta feedback.</B> When you send feedback, we store your message, any screenshot you attach, your app version, the screen you were on and your phone type. Your name is included only if you leave that option ticked.</>,
        ]} />

        <H>3. What we don't do</H>
        <UL items={[
          "No advertising, no ad IDs, no analytics or tracking tools.",
          "We don't sell, rent or trade your information.",
          "We don't use your information for anything except running Spotter.",
          "We don't need, and don't ask for, access to your contacts, location or Health data. Steps and Watch data come only from Shortcuts you set up yourself.",
        ]} />

        <H>4. How long we keep it</H>
        <P>Shared Spotter information is kept while your Spotter is in use. To limit what's stored, Spotter keeps your most recent workouts and only the last 60 Watch sessions and step entries. Feedback is kept while Spotter is in beta. Information on your phone stays until you remove it or uninstall the app.</P>

        <H>5. Your choices and deleting your data</H>
        <UL items={[
          <>Edit or delete any activity in <B>Progress → Activity history</B>.</>,
          <>Remove a person from a Spotter in <B>Settings → People in this Spotter</B>.</>,
          <><B>Leave this Spotter on this phone</B> in Settings removes the shared data from that phone.</>,
          <>To have your Spotter or your feedback <B>deleted from our servers</B>, email <Mail subject="Spotter data deletion" /> with your Spotter code. We'll delete it within 30 days and confirm.</>,
        ]} />

        <H>6. Children</H>
        <P>Spotter is made for adults aged 18 and over and isn't directed at children or teenagers.</P>

        <H>7. Security</H>
        <P>All information travels over encrypted HTTPS connections, and your Spotter file is stored in private storage that only the app can reach. Spotter codes are random. Keep yours private, because the code is what gives access to your Spotter.</P>

        <H>8. Changes</H>
        <P>If this policy changes, we'll update it on this page and change the date at the top. Important changes will also be shown in the app's "What's new".</P>

        <H>9. Contact</H>
        <P>Questions or requests: <Mail /> · Breezy Labs, Adelaide, South Australia.</P>
      </article>
      <Footer />
    </div>
  );
}
